import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@src/global/store/hooks';
import { entitlementsSelector } from '@src/global/store/slices/entitlements/entitlementsSlice';
import { getEntitlements } from '@src/global/store/slices/entitlements/extraReducers/getEntitlements';
import { Entitlement } from '@src/global/requests/entitlements';
import { ENTITLEMENT_POLLING_CONFIG } from '@src/global/App/constants/constants';
import { logError, logInfo } from '@src/global/utils/log';
import {
  hasActiveEntitlementForSku,
  hasActiveEntitlementForSite,
} from './utils';

interface UseEntitlementPollingProps {
  productSku?: string | null;
  sitePrefix?: string | null;
  enabled?: boolean;
  maxAttempts?: number;
  intervalMs?: number;
}

interface UseEntitlementPollingReturn {
  loading: boolean;
  error: boolean;
  hasActiveEntitlement: boolean;
  entitlements: Entitlement[];
}

export const useEntitlementPolling = ({
  productSku,
  sitePrefix,
  enabled = true,
  maxAttempts = ENTITLEMENT_POLLING_CONFIG.MAX_ATTEMPTS,
  intervalMs = ENTITLEMENT_POLLING_CONFIG.INTERVAL_MS,
}: UseEntitlementPollingProps): UseEntitlementPollingReturn => {
  const dispatch = useAppDispatch();
  const {
    entitlements,
    error: entitlementError,
    errorMessage,
  } = useAppSelector(entitlementsSelector);

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);

  const interval = useRef<number | null>(null);
  const attemptCount = useRef(0);
  const fetchEntitlements = useCallback(() => {
    dispatch(getEntitlements());
  }, [dispatch]);

  const stopPolling = useCallback(() => {
    if (interval.current) {
      logInfo('Clearing interval');
      clearInterval(interval.current);
      interval.current = null;
    }
    setLoading(false);
  }, []);

  const repeatedCallback = useCallback(() => {
    attemptCount.current++;

    if (attemptCount.current >= maxAttempts) {
      logInfo('Max attempts reached, stopping polling');
      setError(true);
      stopPolling();
      return;
    }

    logInfo(
      `Fetching entitlements (attempt ${attemptCount.current}/${maxAttempts})`
    );

    setLoading(true);
    fetchEntitlements();
  }, [maxAttempts, stopPolling, fetchEntitlements]);
  useEffect(() => {
    // Don't poll if both productSku and sitePrefix are null/empty
    const hasValidationCriteria =
      (productSku && productSku.trim() !== '') ||
      (sitePrefix && sitePrefix.trim() !== '');

    if (!enabled || !hasValidationCriteria) {
      setLoading(false);
      setError(false);
      stopPolling();
      return;
    }

    setLoading(true);
    fetchEntitlements();

    if (!interval.current) {
      logInfo('Setting up interval to fetch entitlements');
      interval.current = window.setInterval(
        () => repeatedCallback(),
        intervalMs
      );
    }

    return () => {
      if (interval.current) {
        clearInterval(interval.current);
        interval.current = null;
      }
    };
  }, [enabled, productSku, sitePrefix]);

  useEffect(() => {
    if (!entitlementError) return;

    if (attemptCount.current < maxAttempts) {
      return;
    }

    logError('Error fetching entitlements:', errorMessage);
    setError(true);
    stopPolling();
  }, [entitlementError]);

  useEffect(() => {
    if (!entitlements?.length) return;

    logInfo('Entitlement data received, stopping polling');
    stopPolling();
  }, [entitlements, stopPolling]);

  useEffect(() => {
    // Check entitlements based on site type:
    // - For AGS/VET: check by site prefix (e.g., "vet_monthly_one")
    // - For SpectraBase/WILA: check by exact product SKU
    const hasActive =
      (Boolean(sitePrefix) &&
        hasActiveEntitlementForSite(entitlements, sitePrefix || null)) ||
      (Boolean(productSku) &&
        hasActiveEntitlementForSku(entitlements, productSku || null));

    if (hasActive) {
      logInfo('Entitlement is active, clearing interval');
      stopPolling();
    }
  }, [entitlements, sitePrefix, productSku]);

  const hasActiveEntitlement = useMemo(() => {
    return (
      (Boolean(sitePrefix) &&
        hasActiveEntitlementForSite(entitlements, sitePrefix || null)) ||
      (Boolean(productSku) &&
        hasActiveEntitlementForSku(entitlements, productSku || null))
    );
  }, [entitlements, sitePrefix, productSku]);

  return {
    loading,
    error,
    hasActiveEntitlement,
    entitlements,
  };
};
